import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { clearStableTabSelection } from '../utils/stableTabStorage';

export type DataSource = 'rtl_legacy' | 'stable';

interface DataSourceContextType {
  dataSource: DataSource;
  setDataSource: (source: DataSource) => void;
  dataSourceLabel: string;
}

const DataSourceContext = createContext<DataSourceContextType | undefined>(undefined);

const DATA_SOURCE_STORAGE_KEY = 'mmec_data_source';

export const DataSourceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Default to stable data source (with live OHLC volume from Theta Terminal).
  const [dataSource, setDataSourceState] = useState<DataSource>(() => {
    const stored = localStorage.getItem(DATA_SOURCE_STORAGE_KEY);
    // Backward compatibility: migrate old "rtl" (New RTL) selection into legacy RTL.
    if (stored === 'rtl') {
      return 'rtl_legacy';
    }
    if (stored === 'rtl_legacy' || stored === 'stable') {
      return stored as DataSource;
    }
    return 'stable';
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(DATA_SOURCE_STORAGE_KEY, dataSource);
  }, [dataSource]);

  const setDataSource = (source: DataSource) => {
    setDataSourceState(source);

    if (source === 'stable') {
      // Stable: no pre-load. User must use Fetch bar (one underlying + expiry). Clear so they choose explicitly.
      localStorage.removeItem('mmec_selected_underlying');
      localStorage.removeItem('mmec_selected_underlyings');
      localStorage.removeItem('mmec_priority_fetch_symbols');
    } else {
      // Legacy RTL: clear stable-only selection so scanner-mode always starts clean.
      localStorage.removeItem('mmec_selected_underlying');
      localStorage.removeItem('mmec_selected_underlyings');
      localStorage.removeItem('mmec_priority_fetch_symbols');
    }
    clearStableTabSelection();

    // Update expiry range default based on data source
    // Legacy RTL: no expiry filter. Stable: weekly default.
    const newExpiryDefault = source === 'stable' ? 'weekly' : '';
    localStorage.setItem('mmec_expiry_range', newExpiryDefault);
    console.log(`📊 [DataSource] Changed to ${source}, expiry filter reset to: ${newExpiryDefault || 'ALL'}`);

    // Dispatch event so DataTable and filters reset (e.g. underlying → 'all' for RTL)
    window.dispatchEvent(new CustomEvent('dataSourceChanged', {
      detail: { source, expiryRange: newExpiryDefault }
    }));
  };

  const dataSourceLabel = dataSource === 'rtl_legacy' ? 'Legacy RTL' : 'Stable Database';

  return (
    <DataSourceContext.Provider value={{ dataSource, setDataSource, dataSourceLabel }}>
      {children}
    </DataSourceContext.Provider>
  );
};

export const useDataSource = (): DataSourceContextType => {
  const context = useContext(DataSourceContext);
  if (!context) {
    throw new Error('useDataSource must be used within a DataSourceProvider');
  }
  return context;
};
